import { useMemo } from 'react';

import { resellerPartners } from '@/config/current-partner';
import { useOrgWhitelabel } from '@/hooks/use-org-whitelabel';
import {
  usePartnerBranding,
  usePartnerBrandingForSlug,
  usePartnerBrandingStatus,
  usePartnerBrandingStatusForSlug,
} from '@/hooks/use-partner-branding';
import { resolveAmbientBrand } from '@/lib/branding/resolve-effective-brand';

/** Runtime brand config: bundled base + optional code reseller + DB overlay. */
export const useEffectiveBranding = () => {
  const orgSlug = useOrgWhitelabel();
  const hostDb = usePartnerBranding();
  const slugDb = usePartnerBrandingForSlug(orgSlug);
  const dbBranding = hostDb ?? slugDb;
  const codeReseller = orgSlug ? (resellerPartners()[orgSlug] ?? null) : null;

  return useMemo(
    () => resolveAmbientBrand(dbBranding, codeReseller, orgSlug),
    [dbBranding, codeReseller, orgSlug],
  );
};

/**
 * True once every DB branding lookup that applies to this render has settled
 * (`'idle'` = no lookup applies, `'resolved'` = fetch landed). Used to gate
 * side effects that must not be stamped with the base brand while the DB
 * overlay is still in flight (e.g. `document.title` on DB-branded partner
 * hosts like `app.payfresco.com`).
 */
export const useIsEffectiveBrandingReady = (): boolean => {
  const orgSlug = useOrgWhitelabel();
  const hostStatus = usePartnerBrandingStatus();
  const slugStatus = usePartnerBrandingStatusForSlug(orgSlug);
  return hostStatus !== 'loading' && slugStatus !== 'loading';
};
